import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BathtubOutlinedIcon from '@mui/icons-material/BathtubOutlined';
import { observer } from 'mobx-react';
import type { MenuPhaseGroup, MenuRoomGroup } from '@/store/wizard.store';
import type { MenuSubstep } from '@/types';
import { HEADER_HEIGHT } from '@/components/layout/Header';

interface SideMenuProps {
  phases: MenuPhaseGroup[];
  currentSubstepId: number | null;
  currentRoomId: number | null;
  onSelectSubstep: (substepId: number, roomId: number | null) => void;
  canConfirm: boolean;
  isBusy: boolean;
  onConfirm: () => void;
}

// Larghezza fissa ispirata al SideMenu legacy (290px espanso). Il collasso
// responsivo del legacy non è riprodotto in questa MVP (nota nel report).
// position: sticky + un'altezza ESATTA (non maxHeight) ancorata sotto
// l'header fanno sì che la lista scorra internamente mentre il bottone
// "Prosegui" resti sempre visibile in fondo, senza dover scrollare la pagina.
const MenuRoot = styled(Box)(({ theme }) => ({
  width: 300,
  flexShrink: 0,
  alignSelf: 'flex-start',
  display: 'flex',
  flexDirection: 'column',
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  top: HEADER_HEIGHT,
  height: `calc(100vh - ${HEADER_HEIGHT}px)`,
}));

function roomSubsteps(room: MenuRoomGroup): MenuSubstep[] {
  return room.steps.flatMap((s) => s.substeps);
}

function phaseStatus(phase: MenuPhaseGroup): 'completed' | 'active' | 'todo' {
  const all = phase.rooms.flatMap(roomSubsteps);
  if (all.every((s) => s.isAnswered)) return 'completed';
  if (all.some((s) => s.isAnswered)) return 'active';
  return 'todo';
}

function roomStatus(room: MenuRoomGroup): 'completed' | 'active' | 'todo' {
  const all = roomSubsteps(room);
  if (all.every((s) => s.isAnswered)) return 'completed';
  if (all.some((s) => s.isAnswered)) return 'active';
  return 'todo';
}

function StatusDot({
  state,
}: {
  state: 'completed' | 'current' | 'todo';
}) {
  if (state === 'completed') {
    return <CheckCircleIcon fontSize="small" color="secondary" aria-hidden="true" />;
  }
  if (state === 'current') {
    return (
      <Box
        aria-hidden="true"
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: 'secondary.main',
        }}
      />
    );
  }
  return (
    <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'divider' }} aria-hidden="true" />
  );
}

export const SideMenu = observer(function SideMenu({
  phases,
  currentSubstepId,
  currentRoomId,
  onSelectSubstep,
  canConfirm,
  isBusy,
  onConfirm,
}: SideMenuProps) {
  const currentPhaseId = phases.find((p) =>
    p.rooms.some((room) =>
      roomSubsteps(room).some(
        (sub) => sub.substepId === currentSubstepId && sub.roomId === currentRoomId,
      ),
    ),
  )?.phaseId;

  const allSubsteps = phases.flatMap((p) => p.rooms).flatMap(roomSubsteps);
  const answered = allSubsteps.filter((s) => s.isAnswered).length;
  const total = allSubsteps.length;
  const pct = total === 0 ? 0 : Math.round((answered / total) * 100);

  const currentRoomLabel = allSubsteps.find(
    (s) => s.substepId === currentSubstepId && s.roomId === currentRoomId,
  )?.roomLabel;

  return (
    <MenuRoot component="nav" aria-label="Navigazione del configuratore">
      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Avanzamento — {answered} di {total} risposte
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          color="secondary"
          sx={{ height: 6, borderRadius: 3 }}
          aria-label={`Avanzamento configurazione: ${pct}%`}
        />
        {currentRoomLabel && (
          <Typography variant="caption" color="secondary.dark" sx={{ mt: 1, display: 'block' }}>
            {currentRoomLabel}
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {phases.map((phase) => {
          const status = phaseStatus(phase);
          const isCurrentPhase = phase.phaseId === currentPhaseId;
          return (
            <Accordion
              key={phase.phaseId}
              defaultExpanded={isCurrentPhase || status !== 'completed'}
              disableGutters
              square
              sx={{
                '&:before': { display: 'none' },
                bgcolor: 'transparent',
                boxShadow: 'none',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label={`Fase ${phase.phaseName}`}
                sx={{
                  bgcolor: isCurrentPhase ? 'background.default' : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <StatusDot state={status === 'completed' ? 'completed' : isCurrentPhase ? 'current' : 'todo'} />
                  <Typography
                    variant="body2"
                    fontWeight={isCurrentPhase ? 700 : 600}
                    sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                  >
                    {phase.phaseName}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {phase.rooms.map((room) => {
                  const isRoomMajor = room.roomId !== null;
                  const rStatus = roomStatus(room);
                  const isCurrentRoom = isRoomMajor && room.roomId === currentRoomId;
                  return (
                    <Box key={room.roomId ?? 'none'}>
                      {isRoomMajor && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pl: 2,
                            pr: 2,
                            py: 1,
                            mt: 0.5,
                            bgcolor: isCurrentRoom ? 'background.default' : 'transparent',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <BathtubOutlinedIcon fontSize="small" sx={{ color: 'secondary.main' }} aria-hidden="true" />
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ textTransform: 'uppercase', letterSpacing: '0.03em', flex: 1 }}
                          >
                            {room.roomLabel}
                          </Typography>
                          <StatusDot state={rStatus === 'completed' ? 'completed' : isCurrentRoom ? 'current' : 'todo'} />
                        </Box>
                      )}
                      <List dense disablePadding>
                        {room.steps.map((step) => (
                          <Box key={step.stepId}>
                            {step.substeps.length > 1 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', pl: 3, pt: 1, textTransform: 'uppercase', letterSpacing: '0.03em' }}
                              >
                                {step.stepName}
                              </Typography>
                            )}
                            {step.substeps.map((substep: MenuSubstep) => {
                              const isCurrent =
                                substep.substepId === currentSubstepId &&
                                substep.roomId === currentRoomId;
                              return (
                                <ListItemButton
                                  key={`${substep.substepId}-${substep.roomId ?? 'none'}`}
                                  selected={isCurrent}
                                  onClick={() => onSelectSubstep(substep.substepId, substep.roomId)}
                                  aria-current={isCurrent ? 'step' : undefined}
                                  sx={{
                                    pl: step.substeps.length > 1 ? 4 : 3,
                                    py: 0.75,
                                    '&.Mui-selected': {
                                      bgcolor: 'background.default',
                                      borderRight: '3px solid',
                                      borderColor: 'secondary.main',
                                    },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <StatusDot
                                      state={
                                        substep.isAnswered ? 'completed' : isCurrent ? 'current' : 'todo'
                                      }
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      fontWeight: isCurrent ? 700 : 400,
                                    }}
                                  >
                                    {substep.substepName}
                                  </ListItemText>
                                </ListItemButton>
                              );
                            })}
                          </Box>
                        ))}
                      </List>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          background:
            'linear-gradient(180deg, rgba(240,239,238,0) 0%, rgba(240,239,238,1) 40%)',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          endIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
          disabled={!canConfirm || isBusy}
          onClick={onConfirm}
          aria-label="Conferma la selezione e prosegui al passo successivo"
        >
          Prosegui
        </Button>
        {!canConfirm && !isBusy && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Completa le scelte per continuare
          </Typography>
        )}
      </Box>
    </MenuRoot>
  );
});
